<template>
  <avs-card>
    <template #title>
      <div class="d-flex flex-column w-100">
        <!-- Titre + stats globales -->
        <div class="d-flex align-center mb-4 flex-wrap">
          <v-icon large class="mr-3" color="primary">mdi-gift-outline</v-icon>
          <div>
            <h2 class="text-h5 mb-0">Gestion des dons</h2>
            <p class="text-caption grey--text mb-0" v-if="totals.count !== null">
              {{ totals.count }} don(s) — {{ formatNumber(totals.amount) }} XOF — {{ formatNumber(totals.liters) }} L
            </p>
          </div>
          <v-spacer />
        </div>

        <!-- Filtres -->
        <v-toolbar flat class="bgTable rounded" dense>
          <v-row dense align="center">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="search"
                dense clearable hide-details outlined
                prepend-inner-icon="mdi-magnify"
                :label="$t('Rechercher (nom, téléphone, UUID...)')"
                @input="debouncedSync"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="selectedCompany"
                :items="companies"
                item-text="name"
                item-value="pk"
                dense clearable hide-details outlined
                prepend-inner-icon="mdi-domain"
                :label="$t('Société')"
                @change="onFiltersChange"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-text="text"
                item-value="value"
                dense clearable hide-details outlined
                prepend-inner-icon="mdi-filter"
                :label="$t('Statut')"
                @change="onFiltersChange"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-menu v-model="menuStart" :close-on-content-click="false" transition="scale-transition" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="$t('Date de début')"
                    prepend-inner-icon="mdi-calendar-start"
                    dense hide-details outlined readonly
                    v-bind="attrs" v-on="on"
                    clearable
                    @click:clear="clearStartDate"
                  />
                </template>
                <v-date-picker v-model="startDate" @input="menuStart = false; onFiltersChange()" color="primary" />
              </v-menu>
            </v-col>

            <v-col cols="12" md="2">
              <v-menu v-model="menuEnd" :close-on-content-click="false" transition="scale-transition" offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    :label="$t('Date de fin')"
                    prepend-inner-icon="mdi-calendar-end"
                    dense hide-details outlined readonly
                    v-bind="attrs" v-on="on"
                    clearable
                    @click:clear="clearEndDate"
                  />
                </template>
                <v-date-picker v-model="endDate" @input="menuEnd = false; onFiltersChange()" color="primary" />
              </v-menu>
            </v-col>

            <v-col cols="auto">
              <avs-button
                icon label fab small
                color="grey"
                iconI="mdi-filter-remove"
                :spantext="$t('Réinitialiser les filtres')"
                @click="resetFilters"
              />
            </v-col>
          </v-row>
        </v-toolbar>

        <!-- Chips filtres actifs -->
        <div class="d-flex flex-wrap mt-2" v-if="hasActiveFilters">
          <v-chip v-if="search" small close class="mr-2 mb-1" @click:close="search = ''; onFiltersChange()">
            Recherche: {{ search }}
          </v-chip>
          <v-chip v-if="selectedCompany" small close class="mr-2 mb-1" @click:close="selectedCompany = null; onFiltersChange()">
            Société: {{ companyName }}
          </v-chip>
          <v-chip v-if="statusFilter" small close class="mr-2 mb-1" @click:close="statusFilter = null; onFiltersChange()">
            Statut: {{ statusLabelFilter(statusFilter) }}
          </v-chip>
          <v-chip v-if="startDate" small close class="mr-2 mb-1" @click:close="clearStartDate()">
            Début: {{ startDate }}
          </v-chip>
          <v-chip v-if="endDate" small close class="mr-2 mb-1" @click:close="clearEndDate()">
            Fin: {{ endDate }}
          </v-chip>
        </div>
      </div>
    </template>

    <template #content>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading && page === 1"
        :items-per-page="-1"
        hide-default-footer
        dense
        class="elevation-0 bgTable"
        @dblclick:row="(_, { item }) => openEditor(item)"
      >
        <!-- Société -->
        <template #item.company_name="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="26" color="primary" class="mr-2">
              <span class="white--text text-caption">
                {{ (item.company_name || '?').charAt(0).toUpperCase() }}
              </span>
            </v-avatar>
            <span class="text-truncate" style="max-width: 130px;">
              {{ item.company_name || '-' }}
            </span>
          </div>
        </template>

        <!-- Bénéficiaire -->
        <template #item.beneficiary="{ item }">
          <div class="d-flex flex-column">
            <div class="font-weight-bold">
              {{ (item.last_name || '') + ' ' + (item.first_name || '') }}
            </div>
            <div class="grey--text text--darken-1 text-caption">
              <v-icon x-small>mdi-phone</v-icon>
              {{ item.phone_number || '—' }}
            </div>
          </div>
        </template>

        <!-- Produit -->
        <template #item.company_product_name="{ item }">
          <v-chip v-if="item.company_product_name" x-small color="info" dark>
            {{ item.company_product_name }}
          </v-chip>
          <span v-else class="grey--text">—</span>
        </template>

        <!-- Litres -->
        <template #item.purchase_liters="{ item }">
          {{ formatNumber(item.purchase_liters) }}
        </template>

        <!-- Montant -->
        <template #item.purchase_amount="{ item }">
          {{ formatNumber(item.purchase_amount) }} XOF
        </template>

        <!-- Progression -->
        <template #item.progress="{ item }">
          <div style="min-width: 160px;">
            <div class="d-flex justify-space-between mb-1 text-caption">
              <span>{{ formatNumber(item.liter_remaining || 0) }} / {{ formatNumber(item.purchase_liters || 0) }} L</span>
              <span>{{ progressPercent(item) }}%</span>
            </div>
            <v-progress-linear
              :value="progressPercent(item)"
              height="8"
              :color="progressColor(item)"
              rounded
            />
          </div>
        </template>

        <!-- Dates -->
        <template #item.created_at="{ item }">
          <span class="text-caption">{{ toDatetime(item.created_at) }}</span>
        </template>
        <template #item.valid_until="{ item }">
          <span class="text-caption">{{ item.valid_until ? toDatetime(item.valid_until) : '—' }}</span>
        </template>

        <!-- Statut -->
        <template #item.status_display="{ item }">
          <v-chip small :color="statusColor(item)" text-color="white">
            {{ statusLabel(item) }}
          </v-chip>
        </template>

        <!-- Toggle blocage -->
        <template #item.block="{ item }">
          <v-switch
            class="mt-0" dense hide-details
            :input-value="item.is_blocked"
            color="error"
            :loading="togglingId === item.id"
            :disabled="togglingId === item.id"
            @change="toggleBlock(item, $event)"
          />
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex justify-center" style="gap: 4px;">
            <avs-button
              icon fab label small color="primary"
              iconI="mdi-pencil"
              :spantext="$t('Editer')"
              @click.stop="openEditor(item)"
            />
            <template v-if="downloadingId === item.id">
              <v-progress-circular indeterminate size="22" width="2" />
            </template>
            <template v-else>
              <avs-button
                icon fab label small color="secondary"
                iconI="mdi-download"
                :spantext="$t('Télécharger la carte')"
                @click.stop="downloadBadge(item)"
              />
            </template>
            <avs-button
              icon fab label small color="error"
              iconI="mdi-delete"
              :spantext="$t('Supprimer')"
              @click.stop="tryDelete(item)"
            />
          </div>
        </template>
      </v-data-table>

      <!-- Loader bas -->
      <div class="py-4 d-flex justify-center" v-if="loadingMore">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <!-- Fin de liste -->
      <div
        v-if="!loading && !loadingMore && !hasMore && items.length"
        class="text-center grey--text text--darken-1 py-4"
      >
        <v-icon small class="mr-1">mdi-check-circle</v-icon>
        Fin de la liste ({{ items.length }} don(s))
      </div>

      <!-- Liste vide -->
      <div v-if="!loading && !items.length" class="text-center py-12">
        <v-icon size="64" color="grey lighten-1">mdi-gift-off-outline</v-icon>
        <p class="grey--text mt-4">Aucun don trouvé</p>
        <avs-button
          color="primary" small
          iconI="mdi-filter-remove"
          spantext="Réinitialiser les filtres"
          @click="resetFilters"
        />
      </div>

      <div ref="sentinel" style="height: 1px;"></div>

      <!-- Dialog édition -->
      <v-dialog v-model="editDialog" max-width="560" persistent>
        <v-card v-if="editDialog">
          <v-card-title class="d-flex align-center">
            <v-icon left>mdi-pencil</v-icon>
            Modifier le don
            <v-spacer />
            <v-btn icon @click="editDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field dense outlined hide-details="auto"
                  v-model="form.first_name" label="Prénom" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field dense outlined hide-details="auto"
                  v-model="form.last_name" label="Nom" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field dense outlined hide-details="auto"
                  v-model="form.phone_number" label="Téléphone" />
              </v-col>
              <v-col cols="12" md="6">
                <v-menu v-model="menuValidUntil" :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.valid_until_date"
                      label="Date de validité"
                      prepend-inner-icon="mdi-calendar-end"
                      dense outlined hide-details readonly
                      v-bind="attrs" v-on="on"
                      clearable
                    />
                  </template>
                  <v-date-picker v-model="form.valid_until_date" @input="menuValidUntil = false" color="primary" />
                </v-menu>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field dense outlined hide-details="auto" type="number"
                  v-model.number="form.purchase_liters" label="Litres initiaux" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field dense outlined hide-details="auto" type="number"
                  v-model.number="form.purchase_amount" label="Montant initial (XOF)" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field dense outlined hide-details="auto" type="number"
                  v-model.number="form.liter_remaining" label="Litres restants" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field dense outlined hide-details="auto" type="number"
                  v-model.number="form.remaining_amount" label="Montant restant" />
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-3">
            <v-spacer />
            <v-btn text @click="editDialog = false">Annuler</v-btn>
            <v-btn color="primary" :loading="saving" @click="saveEdit">Enregistrer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog suppression -->
      <v-dialog v-model="deleteDialog" max-width="420">
        <v-card v-if="deleteDialog">
          <v-card-title class="error white--text">
            <v-icon left color="white">mdi-alert</v-icon>
            Supprimer ce don ?
          </v-card-title>
          <v-card-text class="pt-4">
            Cette action est irréversible. Le don sera définitivement supprimé.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="deleteDialog = false">Annuler</v-btn>
            <v-btn color="error" :loading="deleting" @click="confirmDelete">Supprimer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import { CompanyService } from "@/services"
import { toDatetime } from "@/utils/datetime"
import debounce from "lodash/debounce"

export default {
  name: "AdminDonations",
  data() {
    return {
      // Filtres
      search: "",
      selectedCompany: null,
      statusFilter: null,
      startDate: "",
      endDate: "",
      menuStart: false,
      menuEnd: false,

      companies: [],

      // Liste
      loading: false,
      loadingMore: false,
      items: [],
      page: 1,
      hasMore: true,
      totals: { amount: 0, liters: 0, count: null },
      observer: null,

      // Actions
      togglingId: null,
      downloadingId: null,

      // Edit
      editDialog: false,
      menuValidUntil: false,
      selected: null,
      form: {
        first_name: "",
        last_name: "",
        phone_number: "",
        purchase_liters: 0,
        purchase_amount: 0,
        liter_remaining: 0,
        remaining_amount: 0,
        valid_until_date: null,
      },
      saving: false,

      // Delete
      deleteDialog: false,
      deleting: false,

      statusOptions: [
        { text: "Non utilisé", value: "unused" },
        { text: "En cours", value: "ongoing" },
        { text: "Expiré", value: "expired" },
        { text: "Bloqué", value: "blocked" },
        { text: "Approuvé", value: "approved" },
        { text: "En attente", value: "pending" },
        { text: "Rejeté", value: "rejected" },
      ],

      headers: [
        { text: "Société", value: "company_name", width: 160 },
        { text: "Bénéficiaire", value: "beneficiary", width: 180 },
        { text: "Produit", value: "company_product_name", width: 120 },
        { text: "Litres", value: "purchase_liters", align: "right", width: 90 },
        { text: "Montant", value: "purchase_amount", align: "right", width: 120 },
        { text: "Progression", value: "progress", width: 180, sortable: false },
        { text: "Créé le", value: "created_at", width: 120 },
        { text: "Validité", value: "valid_until", width: 120 },
        { text: "Statut", value: "status_display", align: "center", width: 110 },
        { text: "Bloqué", value: "block", align: "center", width: 80, sortable: false },
        { text: "", value: "actions", align: "center", sortable: false, width: 140 },
      ],
    }
  },
  computed: {
    hasActiveFilters() {
      return !!(this.search || this.selectedCompany || this.statusFilter || this.startDate || this.endDate)
    },
    companyName() {
      const c = this.companies.find(x => x.pk === this.selectedCompany)
      return c?.name || ""
    },
  },
  created() {
    this.debouncedSync = debounce(this.onFiltersChange, 350)
    this.fetchCompanies()
  },
  mounted() {
    this.fetchReset()
    this.$nextTick(this.createObserver)
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    toDatetime,
    formatNumber(v) {
      const n = Number(v || 0)
      if (!Number.isFinite(n)) return "0"
      return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },

    clearStartDate() { this.startDate = ""; this.onFiltersChange() },
    clearEndDate() { this.endDate = ""; this.onFiltersChange() },

    fetchCompanies() {
      CompanyService.getAllCompanies()
        .then(r => { this.companies = r || [] })
        .catch(() => { this.companies = [] })
    },

    // --- Statut ---
    r2(v) {
      const n = Number(v)
      return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0
    },
    progressPercent(item) {
      const pl = this.r2(item?.purchase_liters)
      const lr = this.r2(item?.liter_remaining)
      if (pl <= 0) return 0
      let consumed = pl - lr
      if (consumed < 0) consumed = 0
      if (consumed > pl) consumed = pl
      return Math.round(Math.max(0, Math.min(100, (consumed / pl) * 100)))
    },
    progressColor(item) {
      const pct = this.progressPercent(item)
      if (pct <= 50) return "success"
      if (pct <= 80) return "warning"
      return "error"
    },
    isExpired(item) {
      const vu = item.valid_until ? new Date(item.valid_until) : null
      const noBalance = (Number(item.liter_remaining || 0) <= 0) || (Number(item.remaining_amount || 0) <= 0)
      return (vu && vu < new Date()) || noBalance
    },
    isOngoing(item) {
      const pl = this.r2(item?.purchase_liters)
      const lr = this.r2(item?.liter_remaining)
      return !this.isExpired(item) && pl > 0 && lr < pl && lr > 0
    },
    isUnused(item) {
      const pl = this.r2(item?.purchase_liters)
      const lr = this.r2(item?.liter_remaining)
      return !this.isExpired(item) && pl > 0 && lr === pl
    },
    statusLabel(item) {
      if (item.is_blocked) return "Bloqué"
      if (this.isExpired(item)) return "Expiré"
      if (this.isOngoing(item)) return "En cours"
      if (this.isUnused(item)) return "Non utilisé"
      return item.status || "—"
    },
    statusColor(item) {
      if (item.is_blocked) return "grey darken-2"
      if (this.isExpired(item)) return "error"
      if (this.isOngoing(item)) return "warning"
      if (this.isUnused(item)) return "success"
      return "primary"
    },
    statusLabelFilter(v) {
      const o = this.statusOptions.find(x => x.value === v)
      return o ? o.text : v
    },

    // --- Filtres ---
    onFiltersChange() {
      this.fetchReset()
    },
    resetFilters() {
      this.search = ""
      this.selectedCompany = null
      this.statusFilter = null
      this.startDate = ""
      this.endDate = ""
      this.onFiltersChange()
    },

    params() {
      const p = { page: this.page, ordering: "-created_at" }
      if (this.search) p.search = this.search
      if (this.selectedCompany) p.company = this.selectedCompany
      if (this.statusFilter) p.status = this.statusFilter
      if (this.startDate) p.start_date = this.startDate
      if (this.endDate) p.end_date = this.endDate
      return p
    },

    async fetch({ reset = false } = {}) {
      if (reset) this.loading = true
      else this.loadingMore = true
      try {
        const data = await AdminServices.getDonations(this, this.params())
        const results = Array.isArray(data) ? data : (data.results || [])
        this.items = reset ? results : this.items.concat(results)
        this.hasMore = !!(data && data.next)
        if (data && data.count !== undefined) this.totals.count = data.count
        if (data && data.totals) {
          this.totals.amount = data.totals.amount || 0
          this.totals.liters = data.totals.liters || 0
          if (this.totals.count === null) this.totals.count = data.totals.count || 0
        }
      } catch (e) {
        if (e?.response?.status === 404) this.hasMore = false
        else {
          this.$store.commit("app/message", {
            msg: this.$t("messages.error.operation_failed"),
            color: "error",
          })
        }
      } finally {
        this.loading = false
        this.loadingMore = false
        this.$nextTick(() => {
          if (this.hasMore && this.observer && this.$refs.sentinel) {
            this.observer.unobserve(this.$refs.sentinel)
            this.observer.observe(this.$refs.sentinel)
          }
        })
      }
    },
    fetchReset() {
      this.page = 1
      this.hasMore = true
      this.items = []
      this.totals = { amount: 0, liters: 0, count: null }
      this.fetch({ reset: true })
    },

    // --- Infinite scroll ---
    createObserver() {
      const el = this.$refs.sentinel
      if (!el) return
      this.observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) this.loadMore() },
        { root: null, rootMargin: "0px", threshold: 0.1 }
      )
      this.observer.observe(el)
    },
    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.page += 1
      this.fetch()
    },

    // --- Actions ---
    toggleBlock(item, newValue) {
      const desired = !!newValue
      this.togglingId = item.id
      AdminServices.toggleDonationBlock(this, item.id, desired)
        .then(r => {
          item.is_blocked = !!r.is_blocked
          item.status = r.status || item.status
          this.$store.commit("app/message", {
            msg: r.message || (desired ? "Don bloqué" : "Don débloqué"),
            color: desired ? "warning" : "success",
          })
        })
        .catch(() => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.error.operation_failed"),
            color: "error",
          })
        })
        .finally(() => { this.togglingId = null })
    },

    downloadBadge(item) {
      this.downloadingId = item.id
      AdminServices.getDonationBadge(this, item.id)
        .then(blobUrl => {
          const link = document.createElement("a")
          link.href = blobUrl
          link.download = `badge_don_${item.id}.png`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)
        })
        .catch(() => {
          this.$store.commit("app/message", {
            msg: "Erreur téléchargement badge",
            color: "error",
          })
        })
        .finally(() => { this.downloadingId = null })
    },

    openEditor(item) {
      this.selected = item
      const vu = item.valid_until ? String(item.valid_until).slice(0, 10) : null
      this.form = {
        first_name: item.first_name || "",
        last_name: item.last_name || "",
        phone_number: item.phone_number || "",
        purchase_liters: Number(item.purchase_liters || 0),
        purchase_amount: Number(item.purchase_amount || 0),
        liter_remaining: Number(item.liter_remaining || 0),
        remaining_amount: Number(item.remaining_amount || 0),
        valid_until_date: vu,
      }
      this.editDialog = true
    },
    async saveEdit() {
      if (!this.selected) return
      this.saving = true
      const payload = {
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        phone_number: this.form.phone_number,
        purchase_liters: this.form.purchase_liters,
        purchase_amount: this.form.purchase_amount,
        liter_remaining: this.form.liter_remaining,
        remaining_amount: this.form.remaining_amount,
        valid_until: this.form.valid_until_date || null,
      }
      try {
        await AdminServices.updateDonation(this, this.selected.id, payload)
        this.$store.commit("app/message", {
          msg: this.$t("messages.success.operation"),
          color: "success",
        })
        this.editDialog = false
        this.fetchReset()
      } catch (e) {
        const msg = e?.response?.data ? JSON.stringify(e.response.data) : "Erreur lors de l'enregistrement"
        this.$store.commit("app/message", { msg, color: "error" })
      } finally {
        this.saving = false
      }
    },

    tryDelete(item) {
      this.selected = item
      this.deleteDialog = true
    },
    async confirmDelete() {
      if (!this.selected) return
      this.deleting = true
      try {
        await AdminServices.deleteDonation(this, this.selected.id)
        this.$store.commit("app/message", {
          msg: "Don supprimé",
          color: "success",
        })
        this.deleteDialog = false
        this.selected = null
        this.fetchReset()
      } catch (e) {
        this.$store.commit("app/message", {
          msg: "Erreur lors de la suppression",
          color: "error",
        })
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.w-100 { width: 100%; }
</style>
